import React, { useRef, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  Settings,
  FileText,
  ChevronDown,
  Home,
  TrendingUp,
  FolderOpen,
  MoreHorizontal,
  Rocket,
  ExternalLink,
} from 'lucide-react';
import { Tooltip } from '../../components/Tooltip';
import { getPublishedPageUrl } from '../../utils/publishedUrl';
import { shouldShowPagePublishButton } from '../../utils/pagePublishStatus';
import type { PublishStatus } from '../../hooks/useUnpublishedSummary';
import type { Site, Page } from '../../schemas/validation';

interface PageEditorNavProps {
  site: Site;
  currentPage: Page;
  siteId: string;
  pageId: string;
  sitePages: Page[];
  isSavingTemplate: boolean;
  isPublishing?: boolean;
  /** Backend publish status for this page (includes layout assignment changes). */
  pagePublishStatus?: PublishStatus;
  onShowSaveTemplateModal: () => void;
  onShowEditPageModal: () => void;
  onShowResponsivePreview: () => void;
  onShowSEOAnalysis: () => void;
  onShowMediaLibrary: () => void;
  onPublish?: () => void;
}

export const PageEditorNav: React.FC<PageEditorNavProps> = ({
  site,
  currentPage,
  siteId,
  pageId,
  sitePages,
  isSavingTemplate,
  isPublishing = false,
  pagePublishStatus,
  onShowSaveTemplateModal,
  onShowEditPageModal,
  onShowResponsivePreview,
  onShowSEOAnalysis,
  onShowMediaLibrary,
  onPublish,
}) => {
  const navigate = useNavigate();
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const pageDropdownRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pageDropdownRef.current && !pageDropdownRef.current.contains(event.target as Node)) {
        setIsPageDropdownOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-white border-b-2 border-gray-300 shadow-md px-4 py-3 sticky top-0 z-[60]">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        {/* Left side: Back link and site/page info */}
        <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
          <Link
            to={`/sites/${siteId}`}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Back to Website</span>
          </Link>
          <div className="text-gray-300 hidden sm:block">|</div>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              {site.name}
            </h1>
            {/* Page Dropdown Navigation */}
            <div className="relative" ref={pageDropdownRef}>
              <button
                onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
                className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 transition-colors group"
              >
                <span className="hidden sm:inline">Editing:</span>
                <span className="font-medium text-gray-700 group-hover:text-gray-900 truncate max-w-[120px] sm:max-w-none">
                  {currentPage.title}
                </span>
                {currentPage.isHomePage && <Home size={12} className="text-blue-500 shrink-0" />}
                {/* Publish status badge */}
                {currentPage.publishedAt ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800 shrink-0">
                    {currentPage.components !== currentPage.publishedComponents
                      ? 'Unpublished changes'
                      : 'Published'}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 shrink-0">
                    Draft
                  </span>
                )}
                <ChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform shrink-0 ${
                    isPageDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isPageDropdownOpen && sitePages.length > 0 && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100] max-h-80 overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    Switch Page
                  </div>
                  {sitePages.map(page => (
                    <button
                      key={page.id}
                      onClick={() => {
                        if (page.id !== pageId) {
                          navigate(`/sites/${siteId}/pages/${page.id}/editor`);
                        }
                        setIsPageDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                        page.id === pageId ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2 min-w-0">
                        <span className="truncate">{page.title}</span>
                        {page.isHomePage && (
                          <Home size={12} className="text-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      {page.id === pageId && (
                        <span className="text-xs text-blue-500 flex-shrink-0">Current</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">
          {/* Primary actions - always visible */}

          {/* Page Settings */}
          <Tooltip content="Page Settings">
            <button
              onClick={onShowEditPageModal}
              className="p-2 text-gray-600 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
            >
              <Settings size={20} />
            </button>
          </Tooltip>

          {/* Preview */}
          <Tooltip content="Preview">
            <button
              onClick={onShowResponsivePreview}
              className="p-2 text-blue-600 hover:text-white hover:bg-blue-600 rounded-lg transition-colors"
            >
              <Eye size={20} />
            </button>
          </Tooltip>

          {/* Secondary actions - hidden on mobile, in dropdown */}
          <Tooltip content="Save as Template">
            <button
              onClick={onShowSaveTemplateModal}
              className="hidden sm:block p-2 text-green-600 hover:text-white hover:bg-green-600 rounded-lg transition-colors"
              disabled={isSavingTemplate}
            >
              <FileText size={20} data-testid="file-text-icon" />
            </button>
          </Tooltip>

          <Tooltip content="Improvements and SEO">
            <button
              onClick={onShowSEOAnalysis}
              className="hidden sm:block p-2 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors"
            >
              <TrendingUp size={20} />
            </button>
          </Tooltip>

          <Tooltip content="Media Library">
            <button
              onClick={onShowMediaLibrary}
              className="hidden sm:block p-2 text-amber-600 hover:text-white hover:bg-amber-600 rounded-lg transition-colors"
            >
              <FolderOpen size={20} />
            </button>
          </Tooltip>

          {/* View Live - shown when page is published */}
          {currentPage.publishedAt &&
            site?.isPublished &&
            (site?.customDomain || site?.subdomain) && (
              <Tooltip content="View Live Page">
                <button
                  onClick={() => {
                    const url = getPublishedPageUrl(
                      site,
                      currentPage.publishedSlug || currentPage.slug,
                      currentPage.isHomePage
                    );
                    window.open(url, '_blank');
                  }}
                  className="hidden sm:block p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-colors"
                >
                  <ExternalLink size={20} />
                </button>
              </Tooltip>
            )}

          {/* Publish button - shown when page has unpublished changes (components, layout, slug, etc.) */}
          {onPublish && shouldShowPagePublishButton(currentPage, pagePublishStatus) && (
            <Tooltip content={currentPage.publishedAt ? 'Publish Changes' : 'Publish Page'}>
              <button
                onClick={onPublish}
                disabled={isPublishing}
                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
              >
                {isPublishing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span className="hidden sm:inline">Publishing...</span>
                  </>
                ) : (
                  <>
                    <Rocket size={16} />
                    <span className="hidden sm:inline">
                      {currentPage.publishedAt ? 'Republish' : 'Publish'}
                    </span>
                  </>
                )}
              </button>
            </Tooltip>
          )}

          {/* More menu - visible only on mobile */}
          <div className="relative sm:hidden" ref={moreMenuRef}>
            <button
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className="p-2 text-gray-600 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
              aria-expanded={isMoreMenuOpen}
              aria-haspopup="true"
            >
              <MoreHorizontal size={20} />
            </button>

            {isMoreMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={() => {
                    setIsMoreMenuOpen(false);
                    onShowSaveTemplateModal();
                  }}
                  disabled={isSavingTemplate}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FileText size={16} className="mr-3 text-green-600" />
                  Save as Template
                </button>
                <button
                  onClick={() => {
                    setIsMoreMenuOpen(false);
                    onShowSEOAnalysis();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <TrendingUp size={16} className="mr-3 text-indigo-600" />
                  Improvements and SEO
                </button>
                <button
                  onClick={() => {
                    setIsMoreMenuOpen(false);
                    onShowMediaLibrary();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FolderOpen size={16} className="mr-3 text-amber-600" />
                  Media Library
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
